import React, { useState } from 'react';

interface AdminResetPasswordCardProps {
  apiBaseUrl: string;
  getAuthHeaders: () => Record<string, string>;
}

interface ResetResult {
  email: string;
  name?: string;
  tempPassword: string;
}

// Admin-only: reset any user's password to a generated temporary one.
// The temporary password is shown exactly once — copy it before leaving the page.
const AdminResetPasswordCard: React.FC<AdminResetPasswordCardProps> = ({ apiBaseUrl, getAuthHeaders }) => {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ResetResult | null>(null);
  const [confirming, setConfirming] = useState(false);

  const handleReset = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${apiBaseUrl}/api/admin/reset-user-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ email: email.trim() })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || 'Could not reset the password. Please try again.');
      } else {
        setResult({ email: data.email, name: data.name, tempPassword: data.tempPassword });
        setEmail('');
      }
    } catch {
      setError('Could not reach the server. Please try again.');
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  };

  return (
    <section className="settings-card">
      <h3>Reset a User's Password (Admin)</h3>
      <p className="settings-hint">
        Generates a temporary password and signs the user out everywhere. Send it to them securely —
        it is shown only once and never stored in readable form.
      </p>

      <div className="form-group">
        <label>User Email</label>
        <input
          type="email"
          value={email}
          onChange={(e) => { setEmail(e.target.value); setError(null); setConfirming(false); }}
          placeholder="user@institution.edu"
          autoComplete="off"
        />
      </div>

      {error && <p className="settings-error">{error}</p>}

      <div className="settings-key-actions">
        {confirming ? (
          <>
            <button className="delete-button" onClick={handleReset} disabled={busy}>
              {busy ? 'Resetting…' : `Confirm Reset for ${email.trim()}`}
            </button>
            <button className="secondary-button" onClick={() => setConfirming(false)} disabled={busy}>
              Cancel
            </button>
          </>
        ) : (
          <button
            className="secondary-button"
            onClick={() => setConfirming(true)}
            disabled={busy || !email.trim()}
          >
            Reset Password
          </button>
        )}
      </div>

      {result && (
        <div className="settings-temp-password">
          <p>
            Temporary password for <strong>{result.name || result.email}</strong> ({result.email}):
          </p>
          <p><code>{result.tempPassword}</code></p>
          <p className="settings-hint">Copy it now — it will not be shown again.</p>
        </div>
      )}
    </section>
  );
};

export default AdminResetPasswordCard;
